export type PieceType = "K" | "Q" | "R" | "B" | "N"
export type BackRank = (PieceType | null)[]

/**
 * Converts a Chess960 position number (1–960) to a back rank arrangement
 * using the Scharnagl numbering scheme.
 * https://en.wikipedia.org/wiki/Fischer_random_chess_numbering_scheme
 */
export function positionNumberToBackRank(n: number): BackRank {
  if (!Number.isInteger(n) || n < 1 || n > 960) {
    throw new RangeError(`Position number must be 1–960, got ${n}`)
  }

  const idx = n - 1 // 0-based index
  const rank: (PieceType | null)[] = Array(8).fill(null)

  // Step 1: Place light-square bishop (squares b1, d1, f1, h1 → indices 1, 3, 5, 7)
  const n2 = idx % 4
  rank[n2 * 2 + 1] = "B"

  // Step 2: Place dark-square bishop (squares a1, c1, e1, g1 → indices 0, 2, 4, 6)
  const q1 = Math.floor(idx / 4)
  const n3 = q1 % 4
  rank[n3 * 2] = "B"

  // Step 3: Place queen in one of the 6 remaining empty squares
  const q2 = Math.floor(q1 / 4)
  const n4 = q2 % 6
  let count = -1
  for (let i = 0; i < 8; i++) {
    if (rank[i] === null) {
      count++
      if (count === n4) {
        rank[i] = "Q"
        break
      }
    }
  }

  // Step 4: Place knights using the KRN table (maps 0–9 to two positions among 5 empty slots)
  const krnIndex = Math.floor(q2 / 6)
  const KRN_TABLE: [number, number][] = [
    [0, 1], [0, 2], [0, 3], [0, 4],
    [1, 2], [1, 3], [1, 4],
    [2, 3], [2, 4],
    [3, 4],
  ]
  const [n1Pos, n2Pos] = KRN_TABLE[krnIndex]

  const emptySlots: number[] = []
  for (let i = 0; i < 8; i++) {
    if (rank[i] === null) emptySlots.push(i)
  }
  // emptySlots has exactly 5 entries
  rank[emptySlots[n1Pos]] = "N"
  rank[emptySlots[n2Pos]] = "N"

  // Step 5: Place R-K-R in the 3 remaining empty slots (left to right)
  const finalSlots: number[] = []
  for (let i = 0; i < 8; i++) {
    if (rank[i] === null) finalSlots.push(i)
  }
  rank[finalSlots[0]] = "R"
  rank[finalSlots[1]] = "K"
  rank[finalSlots[2]] = "R"

  return rank
}

const FULL_INVENTORY: Record<PieceType, number> = {
  K: 1,
  Q: 1,
  R: 2,
  B: 2,
  N: 2,
}

function countPieces(rank: BackRank): Record<PieceType, number> {
  const counts: Record<PieceType, number> = { K: 0, Q: 0, R: 0, B: 0, N: 0 }
  for (const piece of rank) {
    if (piece) counts[piece]++
  }
  return counts
}

function remainingPieces(rank: BackRank): Record<PieceType, number> {
  const counts = countPieces(rank)
  const remaining: Record<PieceType, number> = { K: 0, Q: 0, R: 0, B: 0, N: 0 }
  for (const p of Object.keys(FULL_INVENTORY) as PieceType[]) {
    remaining[p] = FULL_INVENTORY[p] - counts[p]
  }
  return remaining
}

/** Returns true if the square index is a dark square (a1=0 is dark, b1=1 is light, etc.) */
function isDarkSquare(index: number): boolean {
  return index % 2 === 0
}

/**
 * Returns which pieces can legally be placed in the given slot,
 * considering piece inventory, bishop color constraints, and king-between-rooks rule.
 */
export function getAvailablePieces(
  rank: BackRank,
  slotIndex: number
): PieceType[] {
  if (rank[slotIndex] !== null) return []

  const remaining = remainingPieces(rank)
  const available: PieceType[] = []

  for (const piece of Object.keys(remaining) as PieceType[]) {
    if (remaining[piece] <= 0) continue
    if (canPlace(rank, slotIndex, piece, remaining)) {
      available.push(piece)
    }
  }

  return available
}

function canPlace(
  rank: BackRank,
  slotIndex: number,
  piece: PieceType,
  remaining: Record<PieceType, number>
): boolean {
  // Bishop color constraint
  if (piece === "B") {
    const dark = isDarkSquare(slotIndex)
    // Check if a bishop is already on a square of the same color
    for (let i = 0; i < 8; i++) {
      if (rank[i] === "B" && isDarkSquare(i) === dark) {
        return false
      }
    }
  }

  // King-between-rooks constraint
  if (piece === "K") {
    return canPlaceKing(rank, slotIndex)
  }

  if (piece === "R") {
    return canPlaceRook(rank, slotIndex, remaining)
  }

  // For Q and N, also verify the placement won't make it impossible to complete
  // the position (king-between-rooks feasibility check)
  const hypothetical = [...rank]
  hypothetical[slotIndex] = piece
  return isFeasible(hypothetical)
}

function canPlaceKing(rank: BackRank, kingIndex: number): boolean {
  const rookPositions: number[] = []
  for (let i = 0; i < 8; i++) {
    if (rank[i] === "R") rookPositions.push(i)
  }

  if (rookPositions.length === 2) {
    // King must be strictly between the two rooks
    const left = Math.min(rookPositions[0], rookPositions[1])
    const right = Math.max(rookPositions[0], rookPositions[1])
    return kingIndex > left && kingIndex < right
  }

  if (rookPositions.length === 1) {
    const rookPos = rookPositions[0]
    // One rook placed. The king needs to eventually be between two rooks.
    // There must be room for another rook on the other side of the king.
    // If king is at the same position or beyond the rook in one direction,
    // there must be an empty slot on the other side for the second rook.
    if (kingIndex === rookPos) return false

    if (kingIndex < rookPos) {
      // King is left of rook. Need an empty slot left of king for second rook,
      // OR the second rook can go further right of the existing rook — no, king must be BETWEEN.
      // Actually: king left of rook means we need second rook left of king.
      const hasEmptyLeft = rank.some(
        (p, i) => i < kingIndex && p === null && i !== kingIndex
      )
      return hasEmptyLeft
    } else {
      // King is right of rook. Need second rook right of king.
      const hasEmptyRight = rank.some(
        (p, i) => i > kingIndex && p === null && i !== kingIndex
      )
      return hasEmptyRight
    }
  }

  // No rooks placed yet. King can go anywhere as long as there's room
  // for rooks on both sides eventually.
  // King can't go on index 0 or 7 (no room for rooks on both sides)
  if (kingIndex === 0 || kingIndex === 7) return false

  // Need at least one empty slot on each side
  const emptyLeft = rank.some((p, i) => i < kingIndex && p === null)
  const emptyRight = rank.some((p, i) => i > kingIndex && p === null)
  return emptyLeft && emptyRight
}

function canPlaceRook(
  rank: BackRank,
  rookIndex: number,
  remaining: Record<PieceType, number>
): boolean {
  const existingRooks: number[] = []
  let kingIndex: number | null = null
  for (let i = 0; i < 8; i++) {
    if (rank[i] === "R") existingRooks.push(i)
    if (rank[i] === "K") kingIndex = i
  }

  if (existingRooks.length === 1 && kingIndex !== null) {
    // This is the second rook. King must be between the two rooks.
    const otherRook = existingRooks[0]
    const left = Math.min(otherRook, rookIndex)
    const right = Math.max(otherRook, rookIndex)
    if (kingIndex <= left || kingIndex >= right) return false
  }

  // If this is the second rook and king isn't placed yet,
  // ensure there's an empty slot between the two rooks for the king
  if (existingRooks.length === 1 && kingIndex === null && remaining.K > 0) {
    const otherRook = existingRooks[0]
    const left = Math.min(otherRook, rookIndex)
    const right = Math.max(otherRook, rookIndex)
    if (right - left < 2) return false // no room for king between
    const hasEmptyBetween = rank.some(
      (p, i) => i > left && i < right && p === null && i !== rookIndex
    )
    if (!hasEmptyBetween) return false
  }

  // Verify feasibility after placement
  const hypothetical = [...rank]
  hypothetical[rookIndex] = "R"
  return isFeasible(hypothetical)
}

/**
 * Checks if a partially-filled rank can still be completed into a valid Chess960 position.
 * This is a lightweight feasibility check.
 */
function isFeasible(rank: BackRank): boolean {
  const remaining = remainingPieces(rank)
  const emptySlots: number[] = []
  for (let i = 0; i < 8; i++) {
    if (rank[i] === null) emptySlots.push(i)
  }

  // Check bishop color feasibility
  if (remaining.B > 0) {
    const placedBishops: number[] = []
    for (let i = 0; i < 8; i++) {
      if (rank[i] === "B") placedBishops.push(i)
    }

    if (remaining.B === 1 && placedBishops.length === 1) {
      // Need to place one more bishop on opposite color
      const placedOnDark = isDarkSquare(placedBishops[0])
      const needDark = !placedOnDark
      const hasSlot = emptySlots.some((i) => isDarkSquare(i) === needDark)
      if (!hasSlot) return false
    }

    if (remaining.B === 2) {
      // Need one dark and one light empty slot
      const hasDark = emptySlots.some((i) => isDarkSquare(i))
      const hasLight = emptySlots.some((i) => !isDarkSquare(i))
      if (!hasDark || !hasLight) return false
    }
  }

  // Check king-between-rooks feasibility
  const rookPositions: number[] = []
  let kingIndex: number | null = null
  for (let i = 0; i < 8; i++) {
    if (rank[i] === "R") rookPositions.push(i)
    if (rank[i] === "K") kingIndex = i
  }

  if (rookPositions.length === 2 && kingIndex !== null) {
    const left = Math.min(rookPositions[0], rookPositions[1])
    const right = Math.max(rookPositions[0], rookPositions[1])
    if (kingIndex <= left || kingIndex >= right) return false
  }

  if (rookPositions.length === 2 && kingIndex === null && remaining.K > 0) {
    const left = Math.min(rookPositions[0], rookPositions[1])
    const right = Math.max(rookPositions[0], rookPositions[1])
    const hasEmptyBetween = emptySlots.some((i) => i > left && i < right)
    if (!hasEmptyBetween) return false
  }

  return true
}

/**
 * Returns true if the rank is a complete, valid Chess960 back-rank position.
 */
export function isValidBackRank(rank: BackRank): boolean {
  if (rank.length !== 8 || rank.some((p) => p === null)) return false

  const counts = countPieces(rank)
  if (
    counts.K !== 1 ||
    counts.Q !== 1 ||
    counts.R !== 2 ||
    counts.B !== 2 ||
    counts.N !== 2
  )
    return false

  // Bishops on opposite colors
  const bishopIndices: number[] = []
  for (let i = 0; i < 8; i++) {
    if (rank[i] === "B") bishopIndices.push(i)
  }
  if (isDarkSquare(bishopIndices[0]) === isDarkSquare(bishopIndices[1]))
    return false

  // King between rooks
  let kingIdx = -1
  const rookIdx: number[] = []
  for (let i = 0; i < 8; i++) {
    if (rank[i] === "K") kingIdx = i
    if (rank[i] === "R") rookIdx.push(i)
  }
  const leftRook = Math.min(rookIdx[0], rookIdx[1])
  const rightRook = Math.max(rookIdx[0], rookIdx[1])
  if (kingIdx <= leftRook || kingIdx >= rightRook) return false

  return true
}

/**
 * Generates a full FEN string from a complete back rank.
 * White pieces are uppercase (rank 1), black pieces are lowercase (rank 8).
 * FEN lists rank 8 first, rank 1 last.
 */
export function backRankToFen(rank: BackRank): string {
  const whitePieces = rank.map((p) => (p as string).toUpperCase()).join("")
  const blackPieces = rank.map((p) => (p as string).toLowerCase()).join("")

  const fenBoard = [
    blackPieces,
    "pppppppp",
    "8",
    "8",
    "8",
    "8",
    "PPPPPPPP",
    whitePieces,
  ].join("/")

  // Derive castling rights from king and rook positions
  let kingIdx = -1
  const rookIdx: number[] = []
  for (let i = 0; i < 8; i++) {
    if (rank[i] === "K") kingIdx = i
    if (rank[i] === "R") rookIdx.push(i)
  }
  const leftRook = Math.min(rookIdx[0], rookIdx[1])
  const rightRook = Math.max(rookIdx[0], rookIdx[1])

  // Standard castling notation: K = kingside (right rook), Q = queenside (left rook)
  let castling = ""
  if (rightRook > kingIdx) castling += "K"
  if (leftRook < kingIdx) castling += "Q"
  if (rightRook > kingIdx) castling += "k"
  if (leftRook < kingIdx) castling += "q"
  if (castling === "") castling = "-"

  return `${fenBoard} w ${castling} - 0 1`
}
